import { useMutation } from '@tanstack/react-query';
import {
  RoutineActivity,
  ScheduledAction,
  SchedulerPreferenceV3,
  TaskItemForSchedule,
  ScheduleLogicElement,
} from '@demind-inc/core';
import dayjs from 'dayjs';
import { createParser, EventSourceMessage, ParseError } from 'eventsource-parser';
import { v4 as uuidv4 } from 'uuid';

import { schedulerApi, API_URL, getOAuthAccessToken, cancelChatJob } from '../api';
import {
  applyWeekPlanDayComplete,
  clearPersistedJobIfMatches,
  clearWeeklyPlanCache,
  isInFlightPlanDisconnect,
  setPersistedJob,
  sortScheduledActionsChronologically,
  toWeeklyPlanTaskRefs,
  writeWeeklyPlanCache,
  WeeklyPlanJobContext,
  WEEKLY_PLAN_JOB_TYPE,
} from '../helpers';
import {
  transformAISchedulerV5ResultToScheduleAction,
  transformAllPlanV7ScheduleEventsToScheduleAction,
} from '../../helpers';

interface UseRunAISchedulerV3Params {
  userId: string;
  metricId: string;
  dataTrendId: string;
  targetTime: string;
  preferences: SchedulerPreferenceV3;
  routine?: RoutineActivity[];
  tasks?: TaskItemForSchedule[];
  logicElements?: ScheduleLogicElement[];
}

interface UseRunAISchedulerV5Params {
  userId: string;
  metricId: string;
  targetDate: string; // YYYY-MM-DD
  preferenceId: string;
  routineId: string;
  routine?: RoutineActivity[];
  tasks?: TaskItemForSchedule[];
  userMessage?: string;
}

interface UseRunAISchedulerV5StreamParams extends UseRunAISchedulerV5Params {
  onStatusText?: (statusText: string) => void;
  onResult?: (result: { schedules: ScheduledAction[]; summary?: string | null }) => void;
  onError?: (error: Error) => void;
  onStreamEnd?: () => void;
}

interface UseRunAIWeekPlanStreamParams {
  userId: string;
  metricId: string;
  weekStart: string; // YYYY-MM-DD
  numDays?: number;
  preferenceId: string;
  routineId: string;
  routine?: RoutineActivity[];
  tasks?: TaskItemForSchedule[];
  userMessage?: string;
  currentDate?: string;
  currentTime?: string;
  onStatusText?: (statusText: string) => void;
  onResult?: (result: { schedules: ScheduledAction[]; summary?: string | null }) => void;
  onDayStart?: (payload: {
    date: string;
    day_name?: string;
    total_days?: number;
    current_index?: number;
  }) => void;
  onDayComplete?: (payload: {
    date: string;
    day_name?: string;
    events?: Array<Record<string, any>>;
    summary?: string | null;
  }) => void;
  onWeekComplete?: (payload: {
    week_start?: string;
    total_events?: number;
    success_days?: number;
    failed_days?: string[];
    summary?: string | null;
  }) => void;
  onError?: (error: Error) => void;
  onStreamEnd?: () => void;
}

export function useRunAIScheduler() {
  const runAISchedulerV5Mutation = useMutation({
    mutationFn: ({
      userId,
      metricId,
      routineId,
      targetDate,
      preferenceId,
      tasks,
      routine,
      userMessage,
    }: UseRunAISchedulerV5Params) => {
      return schedulerApi
        .runAISchedulerV5(userId, {
          metricId,
          targetDate,
          preferencesId: preferenceId,
          routineId,
          todos: tasks,
          routine,
          userMessage,
        })
        .then(({ data }) => ({
          schedules: (data.schedules || []) as ScheduledAction[],
          scienceApiReq: data.scienceApiReq!,
          scienceApiRes: data.scienceApiRes!,
          summary: data.summary ?? null,
        }));
    },
  });

  const runAISchedulerV5Stream = async ({
    userId,
    metricId,
    routineId,
    targetDate,
    preferenceId,
    tasks,
    routine,
    userMessage,
    onStatusText,
    onResult,
    onError,
    onStreamEnd,
    abortController,
  }: UseRunAISchedulerV5StreamParams & { abortController?: AbortController }): Promise<{
    schedules: ScheduledAction[];
    scienceApiReq?: string;
    scienceApiRes?: string;
    summary?: string | null;
  }> => {
    return new Promise((resolve, reject) => {
      const streamPath = '/api/v1/scheduler/aiScheduler/v6/stream';
      const url = new URL(`${API_URL}${streamPath}`);
      url.searchParams.append('userId', userId);

      const oAuthAccessToken = getOAuthAccessToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'X-Device-Type': 'web',
        'X-Time-Zone': dayjs.tz.guess(),
        'X-User-Id': userId,
      };

      if (oAuthAccessToken) {
        headers['Authorization'] = `Bearer ${oAuthAccessToken}`;
      }

      // Handle abort
      if (abortController) {
        abortController.signal.addEventListener('abort', () => {
          onStatusText?.(null);
          onStreamEnd?.();
          reject(new Error('Stream aborted'));
        });
      }

      fetch(url.toString(), {
        method: 'POST',
        headers,
        signal: abortController?.signal,
        body: JSON.stringify({
          metricId,
          targetDate,
          preferencesId: preferenceId,
          routineId,
          todos: tasks,
          routine,
          userMessage,
        }),
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('Response body is not readable');
          }

          const decoder = new TextDecoder();
          let result: {
            schedules: ScheduledAction[];
            scienceApiReq?: string;
            scienceApiRes?: string;
            summary?: string | null;
          } = {
            schedules: [],
          };

          const parser = createParser({
            onEvent(event: EventSourceMessage) {
              try {
                if (event.event === 'status_text') {
                  let statusText: string | null = null;
                  if (event.data) {
                    try {
                      const maybeObject = JSON.parse(event.data);
                      statusText =
                        maybeObject['status_text'] ??
                        maybeObject['statusText'] ??
                        maybeObject['status'] ??
                        (typeof maybeObject === 'string' ? maybeObject : null);
                    } catch {
                      // If parsing fails, treat data as string
                      statusText = event.data;
                    }
                  }
                  if (statusText) {
                    onStatusText?.(statusText);
                  }
                } else if (event.event === 'plan_action') {
                  try {
                    const data = JSON.parse(event.data);
                    const action = data?.action ?? 'update';
                    const item = data?.item ?? '';
                    const time = data?.time ? ` (${data.time})` : '';
                    const reason = data?.reason ? ` — ${data.reason}` : '';
                    const statusText = `${action}${item ? `: ${item}` : ''}${time}${reason}`.trim();
                    if (statusText) {
                      onStatusText?.(statusText);
                    }
                  } catch (parseError) {
                    console.error('Failed to parse plan_action event:', parseError);
                  }
                } else if (event.event === 'result_json' || event.event === 'chat_result') {
                  const data = JSON.parse(event.data);
                  result = {
                    schedules: sortScheduledActionsChronologically(
                      transformAllPlanV7ScheduleEventsToScheduleAction(
                        data.schedules || [],
                        targetDate,
                        tasks || []
                      )
                    ),
                    summary: data.summary ?? null,
                  };
                  onResult?.(result);
                } else if (event.event === 'stream_end' || event.event === 'close') {
                  onStreamEnd?.();
                  resolve(result);
                }
              } catch (parseError) {
                console.error('Failed to parse event data:', parseError);
              }
            },
            onError(error: ParseError) {
              console.error('EventSource parsing error:', error);
              onError?.(error);
            },
          });

          try {
            while (true) {
              // Check if aborted
              if (abortController?.signal.aborted) {
                reader.cancel();
                onStatusText?.(null);
                onStreamEnd?.();
                reject(new Error('Stream aborted'));
                return;
              }

              const { value, done } = await reader.read();
              if (done) {
                resolve(result);
                break;
              }
              const chunk = decoder.decode(value, { stream: true });
              parser.feed(chunk);
            }
          } catch (error) {
            // Don't call onError if it was aborted
            if (error instanceof Error && error.name === 'AbortError') {
              onStatusText?.(null);
              onStreamEnd?.();
              reject(error);
              return;
            }
            onError?.(error as Error);
            reject(error);
          }
        })
        .catch((error) => {
          // Don't call onError if it was aborted
          if (
            error instanceof Error &&
            (error.name === 'AbortError' || error.message === 'Stream aborted')
          ) {
            onStatusText?.(null);
            onStreamEnd?.();
            reject(error);
            return;
          }
          onError?.(error as Error);
          reject(error);
        });
    });
  };

  const runAIWeekPlanStream = async ({
    userId,
    metricId,
    weekStart,
    numDays,
    preferenceId,
    routineId,
    tasks,
    routine,
    userMessage,
    currentDate,
    currentTime,
    onStatusText,
    onResult,
    onDayStart,
    onDayComplete,
    onWeekComplete,
    onError,
    onStreamEnd,
    abortController,
  }: UseRunAIWeekPlanStreamParams & { abortController?: AbortController }): Promise<{
    schedules: ScheduledAction[];
    scienceApiReq?: string;
    scienceApiRes?: string;
    summary?: string | null;
  }> => {
    return new Promise((resolve, reject) => {
      // v2 runs as a background job on science: generation keeps going if this
      // tab closes, and useWeeklyPlanJobRecovery resumes it from the job id
      // persisted below (written before the request so a reload can't miss it).
      const streamPath = '/api/v1/scheduler/week_plan/v2';
      const url = new URL(`${API_URL}${streamPath}`);
      const jobId = uuidv4();
      const jobContext: WeeklyPlanJobContext = {
        weekStart,
        numDays: numDays ?? 5,
        tasks: toWeeklyPlanTaskRefs(tasks),
      };
      setPersistedJob(WEEKLY_PLAN_JOB_TYPE, jobId, jobContext);
      // This run supersedes whatever the last completed week showed.
      clearWeeklyPlanCache();
      const finishJob = () => clearPersistedJobIfMatches(WEEKLY_PLAN_JOB_TYPE, jobId);

      const oAuthAccessToken = getOAuthAccessToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'X-Device-Type': 'web',
        'X-Time-Zone': dayjs.tz.guess(),
        'X-User-Id': userId,
      };

      if (oAuthAccessToken) {
        headers['Authorization'] = `Bearer ${oAuthAccessToken}`;
      }

      if (abortController) {
        abortController.signal.addEventListener('abort', () => {
          // An in-process abort is an explicit stop (or a superseding run); a
          // page reload never reaches here. Stop the server job so it does not
          // keep generating a plan nobody is waiting for.
          finishJob();
          void cancelChatJob(jobId);
          onStatusText?.(null);
          onStreamEnd?.();
          reject(new Error('Stream aborted'));
        });
      }

      fetch(url.toString(), {
        method: 'POST',
        headers,
        signal: abortController?.signal,
        body: JSON.stringify({
          weekStart,
          numDays,
          userId,
          metricId,
          preferencesId: preferenceId,
          routineId,
          todos: tasks,
          routine,
          userMessage,
          currentDate,
          currentTime,
          jobId,
        }),
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('Response body is not readable');
          }

          const decoder = new TextDecoder();
          let result: {
            schedules: ScheduledAction[];
            scienceApiReq?: string;
            scienceApiRes?: string;
            summary?: string | null;
          } = {
            schedules: [],
          };
          // Written once the run finishes successfully so a reload afterwards
          // has a result to restore — the pending-job record is gone by then.
          // Skipped when nothing was scheduled, so a silent failure can't
          // overwrite a good cache entry with an empty one.
          const cacheResult = () => {
            if (result.schedules.length === 0) {
              return;
            }
            writeWeeklyPlanCache({
              weekStart,
              numDays: numDays ?? 5,
              schedules: result.schedules,
              summary: result.summary ?? null,
              completedAt: Date.now(),
            });
          };

          const parser = createParser({
            onEvent(event: EventSourceMessage) {
              try {
                if (event.event === 'status_text') {
                  let statusText: string | null = null;
                  if (event.data) {
                    try {
                      const maybeObject = JSON.parse(event.data);
                      statusText =
                        maybeObject['status_text'] ??
                        maybeObject['statusText'] ??
                        maybeObject['status'] ??
                        (typeof maybeObject === 'string' ? maybeObject : null);
                    } catch {
                      statusText = event.data;
                    }
                  }
                  if (statusText) {
                    onStatusText?.(statusText);
                  }
                } else if (event.event === 'plan_action') {
                  try {
                    const data = JSON.parse(event.data);
                    const action = data?.action ?? 'update';
                    const item = data?.item ?? '';
                    const time = data?.time ? ` (${data.time})` : '';
                    const reason = data?.reason ? ` — ${data.reason}` : '';
                    const statusText = `${action}${item ? `: ${item}` : ''}${time}${reason}`.trim();
                    if (statusText) {
                      onStatusText?.(statusText);
                    }
                  } catch (parseError) {
                    console.error('Failed to parse plan_action event:', parseError);
                  }
                } else if (event.event === 'day_start') {
                  const data = JSON.parse(event.data);
                  onDayStart?.(data);
                } else if (event.event === 'day_complete') {
                  const data = JSON.parse(event.data);
                  result = {
                    ...result,
                    ...applyWeekPlanDayComplete(
                      { schedules: result.schedules, summary: result.summary ?? null },
                      data,
                      weekStart,
                      toWeeklyPlanTaskRefs(tasks)
                    ),
                  };
                  onDayComplete?.(data);
                  onResult?.(result);
                } else if (event.event === 'week_complete') {
                  const data = JSON.parse(event.data);
                  result = {
                    ...result,
                    summary: data.summary ?? result.summary ?? null,
                  };
                  onWeekComplete?.(data);
                  onResult?.(result);
                } else if (event.event === 'result_json' || event.event === 'chat_result') {
                  const data = JSON.parse(event.data);
                  result = {
                    schedules: sortScheduledActionsChronologically(
                      transformAllPlanV7ScheduleEventsToScheduleAction(
                        data.schedules || [],
                        weekStart,
                        tasks || []
                      )
                    ),
                    summary: data.summary ?? null,
                  };
                  onResult?.(result);
                } else if (event.event === 'stream_end' || event.event === 'close') {
                  finishJob();
                  cacheResult();
                  onStreamEnd?.();
                  resolve(result);
                }
              } catch (parseError) {
                console.error('Failed to parse event data:', parseError);
              }
            },
            onError(error: ParseError) {
              console.error('EventSource parsing error:', error);
              onError?.(error);
            },
          });

          try {
            while (true) {
              if (abortController?.signal.aborted) {
                reader.cancel();
                onStatusText?.(null);
                onStreamEnd?.();
                reject(new Error('Stream aborted'));
                return;
              }

              const { value, done } = await reader.read();
              if (done) {
                finishJob();
                cacheResult();
                resolve(result);
                break;
              }
              const chunk = decoder.decode(value, { stream: true });
              parser.feed(chunk);
            }
          } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
              onStatusText?.(null);
              onStreamEnd?.();
              reject(error);
              return;
            }
            if (!isInFlightPlanDisconnect(error)) {
              finishJob();
            }
            onError?.(error as Error);
            reject(error);
          }
        })
        .catch((error) => {
          if (
            error instanceof Error &&
            (error.name === 'AbortError' || error.message === 'Stream aborted')
          ) {
            onStatusText?.(null);
            onStreamEnd?.();
            reject(error);
            return;
          }
          if (!isInFlightPlanDisconnect(error)) {
            finishJob();
          }
          onError?.(error as Error);
          reject(error);
        });
    });
  };

  return {
    runAISchedulerV5: runAISchedulerV5Mutation.mutateAsync,
    runAISchedulerV5Stream,
    runAIWeekPlanStream,
  };
}
